import type { DeliveryOrderStatus, DistanceEtaResult } from "./driver-api.ts";
import type { ChipClass } from "./shift-readiness.ts";

/**
 * `4 To pickup` — the leg to the pickup, as the design draws it.
 *
 * The design's screen, value for value from `11 Driver App.dc.html`:
 *
 *     nav:{l:'4 To pickup',map:1,on:1,chip:['active','to pickup'],
 *       title:'Masaki Mall, loading bay',
 *       sub:'1.2 km · 4 min · background location on',
 *       rows:[['Contact','Merchant desk (via support)']],
 *       btns:[['I\'ve arrived','where_to_vote',1,'slide']]}
 *
 * `tests/contract/mobile-screen-support.test.ts` marks it **degraded** with exactly one element
 * missing, and it is the row:
 *
 *   > `Contact · Merchant desk (via support)` — only
 *   > `POST /internal/v1/tenants/{tenantId}/support-cases` exists, and the gateway does not expose
 *   > `/internal/v1`
 *
 * So the row is drawn and its value is the design's own `unknown` mark rather than a telephone
 * number, a name or a dash. ADR 0029 is why that absence is a decision rather than a delay: the
 * gateway refuses to route `/internal/v1` at all, so SUP-01's case write is not merely unpublished —
 * it is deliberately unreachable from a handset.
 *
 * ## The headline is three hops, and every one of them is published
 *
 * The screen holds an `orderId`. DEL-03 publishes `order.pickupAddressId`, and
 * [ADR 0035](../../../docs/adr/0035-an-address-is-a-record-geo-02-owns-and-re-resolves.md) made that
 * identifier resolvable: `GET /v1/addresses/{addressId}` answers `formattedAddress` and the
 * coordinate it was resolved from. That coordinate and this device's own position are GEO-04's two
 * ends, which is `1.2 km · 4 min`.
 *
 * That chain is the whole reason the pickup is a read rather than a label. Q-53 asked whether an
 * address is a thing this platform owns; ADR 0035 answered yes, and **this screen is one of the two
 * things that answer closed** — while closing none of the three multi-stop screens, because a stop
 * carries an opaque string instead of an identifier (Q-118). An address read answers a screen
 * holding an address identifier and cannot answer one holding a 200-character reference to nothing.
 */

/** The scopes this screen spends, each named once so the derived client grant can see it. */
export const ORDER_READ_SCOPE = "delivery:order:read";
export const ORDER_TRANSITION_SCOPE = "delivery:order:operations";
export const ADDRESS_READ_SCOPE = "address:read";
export const DISTANCE_SCOPE = "distance-eta:query";
export const LOCATION_WRITE_SCOPE = "driver-location:write";

/**
 * What `I've arrived` transitions the order to.
 *
 * DEL-03's lifecycle is `accepted → pickup_ready → in_custody → delivery_attempted → delivered`,
 * and its own `allowed` table admits exactly one non-terminal step out of `accepted`. A courier
 * standing at the pickup is what makes the order ready to be picked up, and taking custody is the
 * next screen's act — `5 Scan`'s _"Parcel par_3N0E is with you"_ is `in_custody` in the design's own
 * words, one screen later.
 *
 * The *value* is this application's reading of the design's button, as `OFFLINE_BY_REQUEST` is of
 * `Go offline`, and is named here rather than written at a call site for the same reason. Q-125.
 */
export const ARRIVED_AT_PICKUP: DeliveryOrderStatus = "pickup_ready";

/**
 * The `reason` DEL-03 requires on a transition.
 *
 * Published and required, and typed as a free string with no published set of values — so the field
 * is not invented and the value is this application's. The same arrangement, and the same reason it
 * is a named constant: Q-64 recorded it for DRV-05's `reasonCode` and this is the second of them.
 */
export const ARRIVED_REASON = "courier_arrived_at_pickup";

/**
 * The travel mode GEO-04 is asked for.
 *
 * Required, with four published values — `car | motorcycle | bicycle | walking` — and nothing on
 * this screen publishes which one this courier is on. The vehicle **assignment** does carry a
 * `vehicleClass`, but it is a free string of up to 120 characters that no contract maps onto
 * GEO-04's four, so deriving the mode from it would be this application inventing that mapping.
 *
 * `motorcycle` is the design's own courier: `11 Driver App.dc.html` draws `two_wheeler` on every map
 * and readiness screen, and `15 WP14`'s vehicle screen prints `Motorcycle T 482 DKX`. It is the
 * approved sample rather than a guess, and it is wrong for a courier in a car — which is Q-126, and
 * is the reason the distance line is flagged when it is drawn.
 */
export const PICKUP_TRAVEL_MODE = "motorcycle" as const;

/**
 * `1.2 km · 4 min · background location on` — the design's sub-line, composed from what answered.
 *
 * Three facts, and each is dropped independently rather than the line being replaced wholesale: a
 * courier who can see the distance and not the time is better served than one who sees neither.
 * With all three the sentence is the design's word for word.
 *
 * **`background location on` is a claim about this device sending its position**, not about a
 * browser permission. The design's notes say "background location shows as a green dot in the status
 * bar whenever the driver is online", and the thing behind it is DRV-06's observation stream — so
 * the phrase is drawn when an observation has actually been accepted, and says the opposite when it
 * has not. Drawing it from `permissions.query` alone would be the readiness screen's mistake: a
 * green row over something nothing checked.
 */
export function pickupSubLine(input: {
  readonly distance: DistanceEtaResult | undefined;
  readonly locationSent: boolean;
  /** True when the device refused to give a position at all, which is a different fact. */
  readonly locationRefused: boolean;
}): { readonly text: string; readonly designed: boolean } {
  const parts: string[] = [];
  if (input.distance !== undefined) {
    parts.push(kilometres(input.distance.distanceMetres), minutes(input.distance.durationSeconds));
  }
  parts.push(
    input.locationSent
      ? "background location on"
      : input.locationRefused
        ? "background location off"
        : "background location not confirmed",
  );
  return {
    text: parts.join(" · "),
    designed: input.distance !== undefined && input.locationSent,
  };
}

/**
 * `1.2 km` from GEO-04's `distanceMetres`.
 *
 * One decimal place, always, which is the design's own figure and is also the honest precision for a
 * routed distance: `1.17 km` claims a metre the provider did not promise, and `1 km` throws away the
 * difference between a two-minute walk and a ten-minute one. Under 100 metres it still reads
 * `0.1 km` rather than switching to metres, because the design gives one unit and switching units
 * mid-journey is a second format for one field — which is the thing `vehicle.ts`'s date note is
 * about. Q-126.
 */
export function kilometres(metres: number): string {
  return `${(metres / 1000).toFixed(1)} km`;
}

/**
 * `4 min` from GEO-04's `durationSeconds`.
 *
 * `Math.ceil`, and the direction is the point — it is the opposite of `nextExpiryDays`'s floor and
 * for the same reasoning applied to a different quantity. A journey of 3 minutes 40 seconds drawn as
 * `3 min` tells a courier they will arrive before they will; drawn as `4 min` it tells them the
 * minute they will arrive **in**. A permit's remaining days round down because the last one is
 * partial; a journey's remaining minutes round up because the last one is still travelled.
 *
 * `durationSeconds` rather than `routeDurationSeconds`: the contract publishes both plus
 * `trafficAdjustmentSeconds`, and `durationSeconds` is the one with the traffic in it. Drawing the
 * route duration would be an ETA that ignores the thing the service went to a provider for.
 */
export function minutes(seconds: number): string {
  const whole = Math.max(1, Math.ceil(seconds / 60));
  return `${String(whole)} min`;
}

/**
 * The state chip. `['active','to pickup']` is the design's, and it is the order's own status.
 *
 * Total over DEL-03's seven published statuses rather than defaulted, so a status added to that
 * lifecycle fails the build here. Only `accepted` is the design's word: that is the status an order
 * is in while a courier is on the way to it, and `pickup_ready` is what pressing the slider makes
 * it, so the screen must be able to draw the state it created. The other five are states this
 * screen can be opened in and the design does not draw, so they take this build's words.
 */
export function pickupChip(status: DeliveryOrderStatus): {
  readonly class: ChipClass;
  readonly label: string;
} {
  switch (status) {
    case "accepted":
      return { class: "active", label: "to pickup" };
    case "pickup_ready":
      return { class: "done", label: "arrived" };
    case "in_custody":
      return { class: "done", label: "with you" };
    case "delivery_attempted":
      return { class: "caution", label: "attempted" };
    case "delivered":
      return { class: "done", label: "delivered" };
    case "cancelled":
      return { class: "closed", label: "cancelled" };
    case "failed":
      return { class: "human", label: "action_required" };
  }
}

/** True when the chip and the slider are in the one state the design draws. */
export function pickupIsDesigned(status: DeliveryOrderStatus): boolean {
  return status === "accepted";
}

/**
 * Whether `I've arrived` can still be pressed, and what to say when it cannot.
 *
 * `undefined` means pressable. Anything else is drawn on the button and said on press, because the
 * design draws one slider on this screen and nothing may be removed — an order already past pickup
 * keeps its slider and explains itself rather than losing it.
 */
export function arrivalUnavailable(status: DeliveryOrderStatus): string | undefined {
  if (status === "accepted") return undefined;
  if (status === "pickup_ready") {
    return "You've already told us you're here. Scanning the parcel is the next step.";
  }
  if (status === "in_custody" || status === "delivery_attempted" || status === "delivered") {
    return "This delivery has moved past the pickup, so arriving at it is no longer a step DEL-03 accepts.";
  }
  return "This delivery is closed, so DEL-03 accepts no further transitions on it.";
}

/** The designed absence: the row is drawn, and its value says what has no route. */
export const CONTACT_ABSENCE = "No public route for a support case";

/** Said in full where there is room for it, which is the press rather than the 36dp pill. */
export const CONTACT_ABSENCE_LONG =
  "Opening a support case is only POST /internal/v1/tenants/{tenantId}/support-cases, and ADR 0029 refuses to route /internal/v1 through the gateway.";

/** Said when the address identifier resolved to nothing, which is not the same as having none. */
export const PICKUP_ADDRESS_UNREADABLE = "This pickup's address couldn't be read";

/** Said when no order is in the address at all, which is not a failure. */
export const NO_ORDER_TITLE = "There's no pickup to show";
export const NO_ORDER_SUB =
  "This screen needs the delivery it's for, and DEL-03 publishes no listing a courier's own token can ask.";

/** Said after a 409 on the slider, which is a read of the current state rather than a retry. */
export const ARRIVAL_CONFLICT_TITLE = "Somebody got there first";
export const ARRIVAL_CONFLICT_SUB =
  "This delivery changed while you were looking at it, so your arrival wasn't applied. Nothing is lost; check the state below.";
